import { inject, provide, ref } from 'vue';
import type { InjectionKey, Ref } from 'vue';
import type { BlockCatalog } from './host';

/** Field definition as served by the types registry (reused by <Field>). */
export interface TypeAttribute {
    slug: string;
    segment?: string | null;
    value?: unknown;
    [key: string]: unknown;
}

/** Container slot metadata mirrored from Base::container(). */
export type ContainerSlots =
    | { mode: 'static'; count: number }
    | { mode: 'attribute'; attribute: string; min: number; max: number };

/** Document-surface representation mirrored from Base::documentNormalized(). */
export type DocumentRepresentation =
    | { kind: 'heading'; attr: string; levelAttr?: string }
    | { kind: 'richtext'; attr: string }
    | { kind: 'plain'; attr: string }
    | { kind: 'component'; component: string }
    | { kind: 'card'; summary?: string[] };

export interface TypeInfo {
    slug: string;
    name: string;
    image?: string | null;
    attributes?: TypeAttribute[];
    container?: { slots: ContainerSlots } | null;
    /** Attribute → selector or {selector, mode}, mirrored from Base::inlineNormalized()
     *  (legacy string|null selectors still accepted defensively). */
    inline?: Record<string, string | { selector: string | null; mode: string } | null> | null;
    /** Document-surface representation; absent = card fallback client-side. */
    document?: DocumentRepresentation | null;
    /** Canvas patching disabled for this type — mirrored from Base::reloadOnly(). */
    reloadOnly?: boolean;
}

export interface SharedBlockInfo {
    id: number | string;
    name: string;
}

/**
 * Type + shared-block registry for the shell (palette, panel fields,
 * labels). Loaded once through the host's BlockCatalog — where the data
 * comes from (page props, endpoints) is the host's business.
 */
export interface BuilderRegistry {
    types: Ref<TypeInfo[]>;
    blocks: Ref<SharedBlockInfo[]>;
    loaded: Ref<boolean>;
    typeOf(slug: string | number): TypeInfo | null;
    blockTitle(id: string | number): string | null;
    defaultValueFor(slug: string): Record<string, unknown>;
    load(): Promise<void>;
}

const REGISTRY_KEY: InjectionKey<BuilderRegistry> = Symbol('builder-v2-registry');

export function createBuilderRegistry(catalog: BlockCatalog): BuilderRegistry {
    const types = ref<TypeInfo[]>([]);
    const blocks = ref<SharedBlockInfo[]>([]);
    const loaded = ref(false);

    const registry: BuilderRegistry = {
        types,
        blocks,
        loaded,
        typeOf(slug) {
            return types.value.find((type) => type.slug === String(slug)) ?? null;
        },
        blockTitle(id) {
            const block = blocks.value.find((item) => String(item.id) === String(id));

            return block ? block.name : null;
        },
        defaultValueFor(slug) {
            const attributes = registry.typeOf(slug)?.attributes ?? [];

            return attributes.reduce<Record<string, unknown>>((value, attribute) => {
                value[attribute.slug] = attribute.value ?? null;

                return value;
            }, {});
        },
        async load() {
            types.value = await catalog.loadTypes();

            if (catalog.loadSharedBlocks) {
                blocks.value = await catalog.loadSharedBlocks();
            }

            loaded.value = true;
        },
    };

    provide(REGISTRY_KEY, registry);

    return registry;
}

export function useBuilderRegistry(): BuilderRegistry {
    const registry = inject(REGISTRY_KEY);

    if (!registry) {
        throw new Error('useBuilderRegistry must be used inside a BuilderV2 tree');
    }

    return registry;
}
